// Single source of truth for subscription transport liveness + delivery evidence.
//
// TWO distinct signals, because an AppSync `ka` keepalive proves the CONNECTION is alive but says nothing
// about whether THIS subscription is still delivering:
//   - liveness  (lastInboundAt): refreshed by EVERY inbound frame incl. `ka`. Read by the watchdog; `ka`
//     flows even when the fridge is idle, so a healthy-but-quiet stream never trips, only a dead socket does.
//   - delivery  (onSubscriptionDelivery): fired ONLY by data / start_ack / connection_ack — real delivery
//     or re-registration evidence. Used to clear a broken-stream flag. A bare `ka` must NOT clear it, or a
//     half-open "connected but not delivering" zombie stays masked.
let lastInboundAt = Date.now();

const deliveryListeners = new Set<() => void>();

// Refresh liveness. Called for every inbound AppSync frame (including `ka` keepalives).
export const markSubscriptionInbound = (): void => {
	lastInboundAt = Date.now();
};

// A real data frame or a (re)registration handshake — proof the subscription itself is delivering, not just
// that the socket is warm. Refreshes liveness too, and notifies delivery listeners.
export const markSubscriptionDelivery = (): void => {
	lastInboundAt = Date.now();
	for (const listener of deliveryListeners) {
		listener();
	}
};

export const getLastInboundAt = (): number => lastInboundAt;

// Re-baseline after a (re)connect so the gap before the first ka/frame can't be mistaken for staleness.
export const resetSubscriptionLiveness = (): void => {
	lastInboundAt = Date.now();
};

// Subscribe to delivery/registration evidence (data, start_ack, connection_ack). Returns an unsubscribe.
// Lets recovery clear a broken-stream flag the moment real delivery resumes — NOT on a bare ka.
export const onSubscriptionDelivery = (listener: () => void): (() => void) => {
	deliveryListeners.add(listener);
	return () => {
		deliveryListeners.delete(listener);
	};
};

export type AppSyncFrameKind = "keepalive" | "delivery" | "other";

// Classify a raw inbound AppSync realtime frame by its `type`. `data`/`start_ack`/`connection_ack` are
// delivery-or-registration evidence; `ka` is connection liveness only; everything else (incl. `complete`/
// `error`/malformed/non-string) is "other" and never counts as delivery evidence.
export const classifyAppSyncFrame = (raw: unknown): AppSyncFrameKind => {
	if (typeof raw !== "string") {
		return "other";
	}
	try {
		const type = (JSON.parse(raw) as { type?: unknown }).type;
		if (type === "ka") {
			return "keepalive";
		}
		if (type === "data" || type === "start_ack" || type === "connection_ack") {
			return "delivery";
		}
		return "other";
	} catch {
		return "other";
	}
};

export type LivenessTickAction = "rebaseline" | "trip" | "healthy";

/**
 * Decide what a watchdog tick should do, given wall-clock `now`, the previous tick time, the last inbound
 * time, and the staleness threshold.
 *
 * `rebaseline` guards against an iOS WKWebView suspend/resume: when the kiosk sleeps, the interval pauses
 * and `Date.now()` jumps, so the first tick on resume sees a gap since the previous tick far larger than
 * the (≤ threshold/4) cadence. That's a clock jump, not a quiet stream — re-baseline rather than trip a
 * false reload. A genuinely dead socket still trips on the NEXT window. (The transport's own
 * onComplete/onError handles the common dead-on-resume case faster anyway; this is the silent-hang backstop.)
 */
export const evaluateLivenessTick = (
	now: number,
	lastTickAt: number,
	lastInboundTime: number,
	staleThresholdMs: number,
): LivenessTickAction => {
	if (now - lastTickAt > staleThresholdMs) {
		return "rebaseline";
	}
	if (now - lastInboundTime >= staleThresholdMs) {
		return "trip";
	}
	return "healthy";
};
